import React from 'react';

/**
 * A minimal in-repo Markdown renderer used by the shared LegalDocument layout.
 *
 * Supports:
 *  - Headings: `#`, `##`, `###`, `####`
 *  - Paragraphs separated by blank lines
 *  - Unordered lists (`-` or `*`) and ordered lists (`1.`), with up to one
 *    level of nesting via 2+ spaces of indentation
 *  - GFM-style tables (header row + `| --- | --- |` separator + body rows)
 *  - Horizontal rule (`---` / `***`)
 *  - Inline `**bold**`, `*italic*`, `` `code` ``
 *  - Links `[text](url)` with sanitized scheme (http, https, mailto)
 *
 * Markup is produced via React elements; we never use dangerouslySetInnerHTML,
 * so raw HTML in the source is rendered as escaped text.
 */

const ALLOWED_LINK_SCHEMES = ['http:', 'https:', 'mailto:'];

const isSafeHref = (href: string): boolean => {
  const trimmed = href.trim();
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) {
    return true;
  }
  try {
    const url = new URL(trimmed);
    return ALLOWED_LINK_SCHEMES.includes(url.protocol);
  } catch {
    return false;
  }
};

type InlineToken =
  | { type: 'text'; value: string }
  | { type: 'bold'; children: InlineToken[] }
  | { type: 'italic'; children: InlineToken[] }
  | { type: 'code'; value: string }
  | { type: 'link'; href: string; children: InlineToken[] };

const tokenizeInline = (input: string): InlineToken[] => {
  const tokens: InlineToken[] = [];
  let buffer = '';
  let i = 0;

  const flushBuffer = () => {
    if (buffer.length > 0) {
      tokens.push({ type: 'text', value: buffer });
      buffer = '';
    }
  };

  while (i < input.length) {
    const ch = input[i];
    const next = input[i + 1];

    if (ch === '\\' && i + 1 < input.length) {
      buffer += next;
      i += 2;
      continue;
    }

    if (ch === '`') {
      const end = input.indexOf('`', i + 1);
      if (end !== -1) {
        flushBuffer();
        tokens.push({ type: 'code', value: input.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }

    if (ch === '*' && next === '*') {
      const end = input.indexOf('**', i + 2);
      if (end !== -1) {
        flushBuffer();
        tokens.push({
          type: 'bold',
          children: tokenizeInline(input.slice(i + 2, end)),
        });
        i = end + 2;
        continue;
      }
    }

    if (ch === '*') {
      const end = input.indexOf('*', i + 1);
      if (end !== -1) {
        flushBuffer();
        tokens.push({
          type: 'italic',
          children: tokenizeInline(input.slice(i + 1, end)),
        });
        i = end + 1;
        continue;
      }
    }

    if (ch === '[') {
      const closingBracket = input.indexOf('](', i + 1);
      if (closingBracket !== -1) {
        const closingParen = input.indexOf(')', closingBracket + 2);
        if (closingParen !== -1) {
          const linkText = input.slice(i + 1, closingBracket);
          const linkHref = input.slice(closingBracket + 2, closingParen);
          flushBuffer();
          tokens.push({
            type: 'link',
            href: linkHref,
            children: tokenizeInline(linkText),
          });
          i = closingParen + 1;
          continue;
        }
      }
    }

    buffer += ch;
    i += 1;
  }

  flushBuffer();
  return tokens;
};

const renderInlineTokens = (
  tokens: InlineToken[],
  keyPrefix: string
): React.ReactNode[] => {
  return tokens.map((token, idx) => {
    const key = `${keyPrefix}-${idx}`;
    switch (token.type) {
      case 'text':
        return <React.Fragment key={key}>{token.value}</React.Fragment>;
      case 'bold':
        return <strong key={key}>{renderInlineTokens(token.children, key)}</strong>;
      case 'italic':
        return <em key={key}>{renderInlineTokens(token.children, key)}</em>;
      case 'code':
        return <code key={key}>{token.value}</code>;
      case 'link': {
        if (!isSafeHref(token.href)) {
          return (
            <React.Fragment key={key}>
              {renderInlineTokens(token.children, key)}
            </React.Fragment>
          );
        }
        return (
          <a key={key} href={token.href.trim()}>
            {renderInlineTokens(token.children, key)}
          </a>
        );
      }
    }
  });
};

const renderInline = (input: string, keyPrefix: string): React.ReactNode[] => {
  return renderInlineTokens(tokenizeInline(input), keyPrefix);
};

const HEADING_REGEX = /^(#{1,4})\s+(.*)$/;
const UNORDERED_LIST_REGEX = /^[-*]\s+(.*)$/;
const ORDERED_LIST_REGEX = /^\d+\.\s+(.*)$/;
const HR_REGEX = /^\s*(-{3,}|\*{3,})\s*$/;
const TABLE_SEPARATOR_REGEX = /^\|(\s*:?-{3,}:?\s*\|)+\s*$/;

interface ListItemNode {
  text: string;
  ordered: boolean;
  children: ListItemNode[];
}

const matchListLine = (
  rawLine: string
): { indent: number; ordered: boolean; text: string } | null => {
  const stripped = rawLine.replace(/^\s+/, '');
  const indent = rawLine.length - stripped.length;
  const unordered = stripped.match(UNORDERED_LIST_REGEX);
  if (unordered) {
    return { indent, ordered: false, text: unordered[1] };
  }
  const ordered = stripped.match(ORDERED_LIST_REGEX);
  if (ordered) {
    return { indent, ordered: true, text: ordered[1] };
  }
  return null;
};

const collectList = (
  lines: string[],
  start: number,
  baseIndent: number
): { items: ListItemNode[]; next: number } => {
  const items: ListItemNode[] = [];
  let i = start;

  while (i < lines.length) {
    const match = matchListLine(lines[i]);
    if (!match || match.indent < baseIndent) break;

    if (match.indent > baseIndent) {
      if (items.length === 0) break;
      const { items: children, next } = collectList(lines, i, match.indent);
      items[items.length - 1].children = children;
      i = next;
      continue;
    }

    items.push({ text: match.text, ordered: match.ordered, children: [] });
    i += 1;
  }

  return { items, next: i };
};

const renderListItems = (
  items: ListItemNode[],
  keyPrefix: string
): React.ReactElement => {
  const ordered = items[0]?.ordered ?? false;
  const ListTag = (ordered ? 'ol' : 'ul') as 'ol' | 'ul';
  return (
    <ListTag key={keyPrefix}>
      {items.map((item, idx) => {
        const liKey = `${keyPrefix}-li-${idx}`;
        return (
          <li key={liKey}>
            {renderInline(item.text, liKey)}
            {item.children.length > 0
              ? renderListItems(item.children, `${liKey}-nested`)
              : null}
          </li>
        );
      })}
    </ListTag>
  );
};

const parseTableRow = (line: string): string[] => {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((cell) => cell.trim());
};

export const renderMarkdown = (source: string): React.ReactNode[] => {
  if (!source) {
    return [];
  }

  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];

  let i = 0;
  let blockKey = 0;

  while (i < lines.length) {
    const rawLine = lines[i];
    const line = rawLine.trim();

    if (line === '') {
      i += 1;
      continue;
    }

    if (HR_REGEX.test(line)) {
      blocks.push(<hr key={`hr-${blockKey++}`} />);
      i += 1;
      continue;
    }

    const headingMatch = line.match(HEADING_REGEX);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const content = headingMatch[2];
      const key = `h${level}-${blockKey++}`;
      const inline = renderInline(content, key);
      switch (level) {
        case 1:
          blocks.push(<h1 key={key}>{inline}</h1>);
          break;
        case 2:
          blocks.push(<h2 key={key}>{inline}</h2>);
          break;
        case 3:
          blocks.push(<h3 key={key}>{inline}</h3>);
          break;
        default:
          blocks.push(<h4 key={key}>{inline}</h4>);
          break;
      }
      i += 1;
      continue;
    }

    // GFM-style table: a header row immediately followed by a separator row.
    if (
      line.startsWith('|') &&
      i + 1 < lines.length &&
      TABLE_SEPARATOR_REGEX.test(lines[i + 1].trim())
    ) {
      const headerCells = parseTableRow(lines[i]);
      i += 2;
      const bodyRows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        bodyRows.push(parseTableRow(lines[i]));
        i += 1;
      }
      const tableKey = `table-${blockKey++}`;
      blocks.push(
        <table key={tableKey}>
          <thead>
            <tr>
              {headerCells.map((cell, idx) => (
                <th key={`${tableKey}-th-${idx}`}>
                  {renderInline(cell, `${tableKey}-th-${idx}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, rIdx) => (
              <tr key={`${tableKey}-tr-${rIdx}`}>
                {row.map((cell, cIdx) => (
                  <td key={`${tableKey}-tr-${rIdx}-td-${cIdx}`}>
                    {renderInline(cell, `${tableKey}-tr-${rIdx}-td-${cIdx}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
      continue;
    }

    if (matchListLine(rawLine)) {
      const baseIndent = matchListLine(rawLine)!.indent;
      const { items, next } = collectList(lines, i, baseIndent);
      const key = `list-${blockKey++}`;
      blocks.push(renderListItems(items, key));
      i = next;
      continue;
    }

    const paragraphLines: string[] = [];
    while (i < lines.length) {
      const currentRaw = lines[i];
      const current = currentRaw.trim();
      if (
        current === '' ||
        HEADING_REGEX.test(current) ||
        matchListLine(currentRaw) ||
        HR_REGEX.test(current) ||
        (current.startsWith('|') &&
          i + 1 < lines.length &&
          TABLE_SEPARATOR_REGEX.test(lines[i + 1].trim()))
      ) {
        break;
      }
      paragraphLines.push(current);
      i += 1;
    }
    if (paragraphLines.length > 0) {
      const key = `p-${blockKey++}`;
      blocks.push(
        <p key={key}>{renderInline(paragraphLines.join(' '), key)}</p>
      );
    }
  }

  return blocks;
};
